/* eslint-disable import/no-unresolved */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import { HrBlockContainer, HrFlexContainer, HrParagraph, HrRed, HrTitleH5 } from '@components/styles/hr-styles';
import { ErrorMessage } from '@hookform/error-message';
import { useCKEditor, prefixEventName } from 'ckeditor4-react';

export const EditorConvertToHTML = ({
  title,
  validate,
  formState,
  control,
  id,
  defVal,
  basicConfig,
  margin,
  width,
  normalConfig,
  disabled,
  ia,
  children
}) => {
  const { errors } = formState;
  const _validate = () => {
    const validateRegister = {};
    if (validate.required) {
      validateRegister.required = 'Campo obligatorio';
    }
    if (validate.minLength > 0) {
      validateRegister.minLength = { value: validate.minLength, message: `La descripción debe ser mas extensa` };
    }
    return validateRegister;
  };
  return (
    <HrBlockContainer margin={margin} width={width}>
      <HrFlexContainer justifyContent='space-between' margin='16px 0 12px 0'>
        <HrTitleH5 isRequired={validate.required}>{title}</HrTitleH5>
        <ErrorMessage
          errors={errors}
          name={id}
          render={({ message }) => <HrParagraph color={HrRed}>{message}</HrParagraph>}
        />
      </HrFlexContainer>
      {ia && <div>{children}</div>}
      <div style={errors[id] ? { border: `1px solid ${HrRed}`, borderRadius: '4px' } : undefined}>
        <Controller
          name={id}
          control={control}
          defaultValue={defVal || ''}
          rules={_validate()}
          render={({ field: { onChange, value } }) => (
            <EditorComponent
              value={value ?? ''}
              onChange={onChange}
              basicConfig={basicConfig}
              normalConfig={normalConfig}
              disabled={disabled}
            />
          )}
        />
      </div>
    </HrBlockContainer>
  );
};

EditorConvertToHTML.propTypes = {
  validate: PropTypes.object,
  formState: PropTypes.object,
  margin: PropTypes.string,
  width: PropTypes.string,
  disabled: PropTypes.bool
};

EditorConvertToHTML.defaultProps = {
  validate: {},
  formState: { errors: {} },
  margin: '0px',
  width: 'auto',
  disabled: false
};

export const EditorComponent = ({ onChange, value, basicConfig, normalConfig, disabled }) => {
  return (
    <Editor
      value={value}
      onChange={onChange}
      basicConfig={basicConfig}
      normalConfig={normalConfig}
      disabled={disabled}
    />
  );
};

const configAdvanced = {
  disableNativeSpellChecker: false,
  toolbar: [
    [
      'Format',
      '-',
      'Bold',
      'Italic',
      'Underline',
      'RemoveFormat',
      '-',
      'TextColor',
      'BGColor',
      '-',
      'JustifyLeft',
      'JustifyCenter',
      'JustifyRight',
      'JustifyBlock',
      '-',
      'NumberedList',
      'BulletedList',
      '-',
      'Outdent',
      'Indent',
      '-',
      'EmojiPanel',
      'Preview'
    ]
  ],
  defaultLanguage: 'es',
  language: 'es',
  extraPlugins: 'colorbutton, justify, emoji, resize',
  removeButtons: 'Subscript,Superscript',
  removePlugins: 'elementspath,contextmenu,liststyle,tabletools,tableselection',
  forcePasteAsPlainText: false,
  pasteFromWordRemoveFontStyles: true,
  pasteFromWordRemoveStyles: true,
  extraAllowedContent: 'p(mso*,Normal)',
  pasteFilter: 'semantic-content',
  emoji_emojiListUrl: `${window.location.origin}/assets/js/react-ckeditor/plugins/emoji/emoji.json`,
  allowedContent: 'h1 h2 h3 h4 h5 h6 ul ol li p strong span a u b i em{*}',
  disallowedContent: '*{font*,margin*,padding*,line-height,box-*,text-indent,white-space,display,-webkit*}'
};

const configNormal = {
  disableNativeSpellChecker: false,
  toolbar: [['Bold', 'Italic', 'Underline', 'RemoveFormat', '-', 'TextColor', 'BGColor', '-', 'EmojiPanel', 'Preview']],
  defaultLanguage: 'es',
  language: 'es',
  extraPlugins: 'colorbutton, justify, emoji, resize',
  removeButtons: 'Subscript,Superscript',
  removePlugins: 'elementspath,contextmenu,liststyle,tabletools,tableselection',
  forcePasteAsPlainText: false,
  pasteFromWordRemoveFontStyles: false,
  pasteFromWordRemoveStyles: false,
  extraAllowedContent: 'p(mso*,Normal)',
  pasteFilter: null,
  emoji_emojiListUrl: `${window.location.origin}/assets/js/react-ckeditor/plugins/emoji/emoji.json`,
  allowedContent: 'p strong span a u b i em{*}',
  disallowedContent: '*{font*,text-align}'
};

const configBasic = {
  disableNativeSpellChecker: false,
  toolbar: [['Bold', 'Italic', 'Underline', 'NumberedList', 'BulletedList', '-', 'Link', 'Unlink']],
  defaultLanguage: 'es',
  language: 'es',
  extraPlugins: 'colorbutton, justify, emoji, resize',
  removeButtons: 'Subscript,Superscript',
  removePlugins: 'elementspath,contextmenu,liststyle,tabletools,tableselection',
  forcePasteAsPlainText: false,
  pasteFromWordRemoveFontStyles: false,
  pasteFromWordRemoveStyles: false,
  extraAllowedContent: 'p(mso*,Normal)',
  pasteFilter: null,
  emoji_emojiListUrl: `${window.location.origin}/assets/js/react-ckeditor/plugins/emoji/emoji.json`,
  allowedContent: 'h1 h2 h3 h4 h5 h6 ul ol li p strong span a em{*}',
  disallowedContent: '*{font*}'
};

/**
 * Normaliza el HTML del editor en dos pasos, en este orden:
 *
 * 1. Todo &nbsp; (y su equivalente U+00A0) pasa a espacio común. Los &nbsp; que
 *    CKEditor deja entre palabras al pegar desde Word no permiten cortar la
 *    línea y desbordan el texto.
 * 2. Los <p> que quedaron sin contenido recuperan su &nbsp;. En un párrafo
 *    vacío el &nbsp; no es un espacio, es el salto de línea: un espacio común
 *    ahí es whitespace colapsable, no genera caja de línea y el <p> mide cero
 *    de alto, así que el salto se pierde al publicar la vacante.
 *
 * El paso 2 solo alcanza a los <p> cuyo contenido es whitespace directo, no a
 * los anidados como <p><span>&nbsp;</span></p>. Esos, y los que ya se guardaron
 * sin &nbsp;, los cubre el min-height de .job-description-content en
 * view-vacancy.css.
 *
 * Tiene que seguir siendo idempotente: el componente compara el valor
 * normalizado del form contra el del editor para decidir si llama a setData(),
 * y si difirieran entraría en un ciclo de re-seteos.
 */
const normalizeEditorValue = (rawValue) =>
  rawValue
    ?.replace(/&nbsp;/g, ' ')
    .replace(/\u00A0/g, ' ')
    .replace(/<p(\s[^>]*)?>\s*<\/p>/gi, '<p$1>&nbsp;</p>') || '';

const Editor = ({ value, onChange, basicConfig, normalConfig, disabled }) => {
  const [element, setElement] = useState();
  const lastEditorValueRef = useRef(normalizeEditorValue(value));

  const config = {
    ...(basicConfig ? configBasic : normalConfig ? configNormal : configAdvanced),
    readOnly: disabled
  };
  const { editor } = useCKEditor({
    config,
    element,
    dispatchEvent: ({ type, payload }) => {
      const editorInstance = payload.editor;

      if (prefixEventName('change') === type) {
        const nextValue = normalizeEditorValue(editorInstance.getData());
        lastEditorValueRef.current = nextValue;
        onChange(nextValue);
      }

      if (prefixEventName('instanceReady') === type) {
        const initialValue = normalizeEditorValue(value);
        const currentValue = normalizeEditorValue(editorInstance.getData());

        if (currentValue !== initialValue) {
          editorInstance.setData(initialValue);
        }

        lastEditorValueRef.current = initialValue;
      }
    },
    subscribeTo: ['instanceReady', 'change'],
    initContent: '',
    editorUrl: `${window.location.origin}/assets/js/react-ckeditor/ckeditor.js`
  });

  useEffect(() => {
    if (!editor) return;

    const normalizedValue = normalizeEditorValue(value);
    const currentEditorValue = normalizeEditorValue(editor.getData());

    if (normalizedValue === lastEditorValueRef.current || normalizedValue === currentEditorValue) {
      lastEditorValueRef.current = normalizedValue;
      return;
    }

    editor.setData(normalizedValue);
    lastEditorValueRef.current = normalizedValue;
  }, [editor, value]);

  return <div ref={setElement} />;
};
